/**
 * 동기화 엔진
 */

import { Filesystem, Directory } from "@capacitor/filesystem";
import { createLogger } from "@ict/shared";
import { getSyncQueue, removeSyncQueueItem, updateSyncQueueItem } from "./changes";
import { getLocalPhoto, updateLocalPhoto } from "./photos";
import { detectConflict, resolveConflict, ConflictResolution } from "./conflict";
import { promisePool } from "./concurrency";
import { retryWithBackoff, type RetryOptions } from "./retry";
import type { SyncQueueItem, ConflictInfo, ItemPatch, CachedMachine } from "./types";
import { uploadImage, dataUrlToFile } from "../upload";
import { saveIdMapping, resolveServerId, getIdMapping } from "./mapping";
import { getRecord, putRecord, deleteRecord, getAllRecords, STORES } from "./index";
import { removeCachedMachine } from "./machines";

const logger = createLogger({ module: "SyncEngine" });

/**
 * 서버 전송용 패치 정리 (클라이언트 전용 필드 제거)
 * oldValue, itemTitle은 로컬 표시용 필드이므로 서버로 전송하지 않음
 */
export function sanitizePatchesForServer(patches: ItemPatch[]): ItemPatch[] {
  return patches.map(({ itemId, itemType, field, value }) => ({
    itemId,
    itemType,
    field,
    value,
    // oldValue, itemTitle 제거
  }));
}

/**
 * 서버 전송용 MachineInfo 패치 정리
 * oldValue는 로컬 표시용 필드이므로 서버로 전송하지 않음
 */
export function sanitizeMachineInfoPatchesForServer(
  patches: Array<{ field: string; value: unknown; oldValue?: unknown }>
): Array<{ field: string; value: unknown }> {
  return patches.map(({ field, value }) => ({
    field,
    value,
    // oldValue 제거
  }));
}

/**
 * 동기화 대상 항목인지 판별
 * - pending 상태만 자동 처리. failed 항목은 사용자가 수동 재시도.
 */
function isSyncable(item: SyncQueueItem): boolean {
  return !item.status || item.status === "pending";
}

interface ItemSyncResult {
  itemId: string;
  status: "synced" | "failed" | "conflict";
  error?: string;
  conflict?: ConflictInfo;
  retryAttempts?: number;
  /** mutation 성공 시 서버가 반환한 새 버전 (그룹 내 버전 전파용) */
  newVersion?: number;
}

interface SyncOptions {
  enableRetry?: boolean;
  retryOptions?: RetryOptions;
  onItemProgress?: (current: number, total: number, item: SyncQueueItem) => void;
}

interface SyncExecutionResult {
  success: boolean;
  syncedCount: number;
  failedCount: number;
  conflictCount: number;
  results: ItemSyncResult[];
  conflicts: ConflictInfo[];
}

/** mutation 함수 반환 타입 (버전 전파용) */
interface MutateResult {
  newVersion?: number;
}

type MutateFunction = (options: { variables: Record<string, unknown> }) => Promise<MutateResult>;

/** 서버 문서 조회 함수 타입 (Feature 326: itemId 매핑용) */
export type FetchMachineDocumentFunction = (
  projectId: string,
  docKey: string,
  machineId: string
) => Promise<{
  checkList: Array<{ id: string }>;
  dataList: Array<{ id: string }>;
  maintenance?: Array<{ id: string }>;
}>;

/**
 * 단일 항목 동기화 (재시도 지원)
 * Feature 326: offline ID 자동 해결 및 ID 매핑 저장
 */
export async function syncSingleItem(
  item: SyncQueueItem,
  mutate: MutateFunction,
  fetchMachineDocument: FetchMachineDocumentFunction,
  options: SyncOptions = {}
): Promise<ItemSyncResult> {
  const { enableRetry = true, retryOptions } = options;
  let retryAttempts = 0;
  let newVersion: number | undefined;

  // 1. offline ID 해결 (Feature 326)
  if (item.machineId?.startsWith("offline-")) {
    const originalMachineId = item.machineId;
    item.machineId = await resolveServerId(item.machineId);
    logger.info(`[ID 해결] machineId: ${originalMachineId} → ${item.machineId}`);
  }
  if (item.entityId?.startsWith("offline-")) {
    const originalEntityId = item.entityId;
    item.entityId = await resolveServerId(item.entityId);
    logger.info(`[ID 해결] entityId: ${originalEntityId} → ${item.entityId}`);
  }

  const executeMutation = async () => {
    const variables = buildMutationVariables(item);
    const result = await mutate({ variables });
    newVersion = result?.newVersion;
    return result;
  };

  try {
    let mutationResult;
    if (enableRetry) {
      await retryWithBackoff(
        async () => {
          mutationResult = await executeMutation();
        },
        {
          maxRetries: 3,
          baseDelayMs: 1000,
          ...retryOptions,
          onRetry: (_error, attempt, _delayMs) => {
            retryAttempts = attempt;
          },
        }
      );
    } else {
      mutationResult = await executeMutation();
    }

    // 2. equipment create 성공 시 ID 매핑 저장 (Feature 326)
    if (item.type === "equipment" && item.operation === "create" && mutationResult) {
      const createdMachineIds = item.createdMachineIds || [];
      // as any 사유: executeMutation의 반환 타입이 FetchResult<Record<string, any>>로
      // createEquipment 필드의 구체적 타입을 컴파일 타임에 알 수 없음
      const serverEquipments = (mutationResult as any)?.data?.createEquipment?.equipments || []; // eslint-disable-line @typescript-eslint/no-explicit-any

      logger.info(
        `[ID 매핑] equipment create 성공 - 로컬 ${createdMachineIds.length}개, 서버 ${serverEquipments.length}개`
      );

      // offline machine ID 배열과 서버 응답 equipments 배열을 순서대로 매핑
      if (createdMachineIds.length > 0 && serverEquipments.length === createdMachineIds.length) {
        for (let i = 0; i < createdMachineIds.length; i++) {
          const localId = createdMachineIds[i];
          const serverId = serverEquipments[i].id;

          if (localId?.startsWith("offline-") && serverId) {
            logger.info(
              `[ID 매핑] [${i + 1}/${createdMachineIds.length}] ${localId} → ${serverId}`
            );

            // 1. Machine ID 매핑 저장
            await saveIdMapping({
              localId,
              serverId,
              entityType: "machine",
            });

            // 2. ItemId 매핑 저장 (checkList, dataList, maintenance)
            await saveItemIdMappings(
              item.projectId,
              item.docKey,
              localId,
              serverId,
              fetchMachineDocument
            );

            // 3. MACHINES 캐시 업데이트
            await updateMachineIdInCache(localId, serverId, item.projectId);

            // 4. 동일 machine의 다른 sync 항목들도 ID 업데이트
            await updateMachineIdInSyncQueue(localId, serverId);

            logger.info(`[ID 매핑] [${i + 1}/${createdMachineIds.length}] 저장 완료`);
          } else {
            logger.warn(
              `[ID 매핑] [${i + 1}/${createdMachineIds.length}] 조건 불일치 - localId: ${localId}, serverId: ${serverId}`
            );
          }
        }

        logger.info(`[ID 매핑] equipment create 매핑 완료 - ${createdMachineIds.length}개 처리됨`);
      } else {
        logger.warn(
          `[ID 매핑] 배열 길이 불일치 - 로컬: ${createdMachineIds.length}, 서버: ${serverEquipments.length}`
        );
      }
    }

    // 3. equipment delete 성공 시 캐시 정리 (#457)
    if (item.type === "equipment" && item.operation === "delete") {
      const docKey = item.docKey || (item.payload.machineKey as string);
      const machineId = item.machineId || (item.payload.machineId as string);
      if (item.projectId && docKey && machineId) {
        await removeCachedMachine(item.projectId, docKey, machineId);
        logger.info("[Sync] equipment delete 캐시 정리 완료", {
          projectId: item.projectId,
          docKey,
          machineId,
        });
      }
    }

    return {
      itemId: item.id,
      status: "synced",
      retryAttempts,
      newVersion,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    return {
      itemId: item.id,
      status: "failed",
      error: errorMessage,
      retryAttempts,
    };
  }
}

/**
 * mutation 변수 빌드
 * 각 타입별로 GraphQL API 형식에 맞게 변환
 * 클라이언트 전용 필드(oldValue, itemTitle)는 서버 전송 전 제거
 */
function buildMutationVariables(item: SyncQueueItem): Record<string, unknown> {
  switch (item.type) {
    case "checklist":
      // UpdateMachineDocumentItemsInput 형식 사용
      return {
        input: {
          projectId: item.projectId,
          docKey: item.docKey,
          machineId: item.machineId,
          expectedVersion: item.expectedVersion ?? 0,
          clientMutationId: `sync-${item.id}-${Date.now()}`,
          patches: item.payload.patches
            ? sanitizePatchesForServer(item.payload.patches as ItemPatch[])
            : [],
        },
      };
    case "data":
      // UpdateMachineDocumentItemsInput 형식 사용 (data도 같은 API)
      return {
        input: {
          projectId: item.projectId,
          docKey: item.docKey,
          machineId: item.machineId,
          expectedVersion: item.expectedVersion ?? 0,
          clientMutationId: `sync-${item.id}-${Date.now()}`,
          patches: item.payload.patches
            ? sanitizePatchesForServer(item.payload.patches as ItemPatch[])
            : [],
        },
      };
    case "photo":
      return {
        projectId: item.projectId,
        docKey: item.docKey,
        machineId: item.machineId,
        file: item.payload.file,
        caption: item.payload.caption,
      };
    case "project":
      return {
        id: item.entityId,
        input: item.payload,
      };
    case "machine":
      // UpdateMachineInfoInput 형식 사용
      return {
        input: {
          projectId: item.projectId,
          docKey: item.docKey,
          machineId: item.machineId,
          expectedVersion: item.expectedVersion ?? 0,
          clientMutationId: `sync-${item.id}-${Date.now()}`,
          patches: item.payload.machineInfoPatches
            ? sanitizeMachineInfoPatchesForServer(
                item.payload.machineInfoPatches as Array<{
                  field: string;
                  value: unknown;
                  oldValue?: unknown;
                }>
              )
            : [],
        },
      };
    case "equipment":
      if (item.operation === "delete") {
        // DeleteEquipmentInput 형식 사용 (#457)
        return {
          input: {
            equipmentOperation: "delete",
            projectId: item.projectId,
            machineKey: item.docKey || item.payload.machineKey,
            machineId: item.machineId || item.payload.machineId,
          },
        };
      }
      // CreateEquipmentInput 형식 사용
      return {
        input: {
          projectId: item.projectId,
          machineKey: item.payload.machineKey,
          count: item.payload.count,
        },
      };
    case "measure":
      // operation별 Measure mutation input 형식 분기
      // measureOperation 필드로 SyncQueueContext에서 명시적 라우팅
      if (item.operation === "create") {
        // AddMeasureInput 형식
        return {
          input: {
            measureOperation: "create",
            projectId: item.projectId,
            docKey: item.docKey,
            machineId: item.machineId,
            measureType: item.payload.measureType,
            data: item.payload.measureData,
            expectedVersion: item.expectedVersion ?? 0,
            clientMutationId: `sync-${item.id}-${Date.now()}`,
          },
        };
      }
      if (item.operation === "delete") {
        // DeleteMeasureInput 형식
        return {
          input: {
            measureOperation: "delete",
            projectId: item.projectId,
            docKey: item.docKey,
            machineId: item.machineId,
            measureType: item.payload.measureType,
            measureIndex: item.payload.measureIndex,
            expectedVersion: item.expectedVersion ?? 0,
            clientMutationId: `sync-${item.id}-${Date.now()}`,
          },
        };
      }
      // UpdateMeasureInput 형식 (기본: update)
      return {
        input: {
          measureOperation: "update",
          projectId: item.projectId,
          docKey: item.docKey,
          machineId: item.machineId,
          measureType: item.payload.measureType,
          measureIndex: item.payload.measureIndex,
          data: item.payload.measureData,
          expectedVersion: item.expectedVersion ?? 0,
          clientMutationId: `sync-${item.id}-${Date.now()}`,
        },
      };
    default:
      return item.payload as Record<string, unknown>;
  }
}

/**
 * 서버 데이터 가져오기 (충돌 감지용)
 */
type FetchServerDataFunction = (entityType: string, entityId: string) => Promise<unknown | null>;

/**
 * 문서 키 생성 (그룹화 기준)
 * 같은 문서에 대한 항목은 순차 처리, 다른 문서 그룹은 병렬 처리
 */
function getDocumentKey(item: SyncQueueItem): string {
  if (item.projectId && item.docKey && item.machineId) {
    return `${item.projectId}_${item.docKey}_${item.machineId}`;
  }
  // 프로젝트/설비 단위 항목은 entityId로 그룹화
  return `${item.type}_${item.entityId}`;
}

/**
 * 동기화 큐 전체 실행
 * 같은 문서 내 항목: 순차 처리 (버전 충돌 방지)
 * 다른 문서 그룹: 병렬 처리 (concurrency: 3)
 */
export async function executeSyncQueue(
  mutate: MutateFunction,
  fetchMachineDocument: FetchMachineDocumentFunction,
  fetchServerData?: FetchServerDataFunction,
  autoResolveConflicts: boolean = false,
  defaultResolution: ConflictResolution = ConflictResolution.KEEP_LOCAL,
  options: SyncOptions = {}
): Promise<SyncExecutionResult> {
  const queue = await getSyncQueue();

  if (queue.length === 0) {
    return {
      success: true,
      syncedCount: 0,
      failedCount: 0,
      conflictCount: 0,
      results: [],
      conflicts: [],
    };
  }

  const results: ItemSyncResult[] = [];
  const conflicts: ConflictInfo[] = [];
  let syncedCount = 0;
  let failedCount = 0;
  let conflictCount = 0;

  const pendingItems = queue.filter((item) => isSyncable(item) && item.type !== "photo");
  const total = pendingItems.length;
  let current = 0;

  // Feature 326: equipment create를 먼저 처리 (ID 매핑 완료 후 update 처리)
  const equipmentCreateItems = pendingItems.filter(
    (item) => item.type === "equipment" && item.operation === "create"
  );

  // 큐 항목 순차 처리 헬퍼 (equipment create/delete 공통)
  const processItemsSequentially = async (items: SyncQueueItem[]) => {
    for (const item of items) {
      current++;
      options.onItemProgress?.(current, total, item);

      const result = await syncSingleItem(item, mutate, fetchMachineDocument, options);
      results.push(result);

      if (result.status === "synced") {
        syncedCount++;
        await updateSyncQueueItem(item.id, {
          status: "completed",
          syncedAt: Date.now(),
        });
      } else {
        failedCount++;
        await updateSyncQueueItem(item.id, {
          status: "failed",
          retryCount: item.retryCount + 1,
          lastError: result.error,
        });
      }
    }
  };

  // equipment create 먼저 순차 처리
  await processItemsSequentially(equipmentCreateItems);

  // #457: equipment delete를 create 이후, 나머지 항목 이전에 순차 처리
  // (create 후 ID 매핑이 완료된 상태에서 삭제 실행)
  const equipmentDeleteItems = pendingItems.filter(
    (item) => item.type === "equipment" && item.operation === "delete"
  );

  await processItemsSequentially(equipmentDeleteItems);

  // Feature 326: equipment create 처리 후 큐를 다시 읽어서 변환된 itemId 반영
  logger.info("[Sync] equipment create/delete 완료 - 큐 다시 로드하여 ID 변환 반영");
  const updatedQueue = await getSyncQueue();
  const updatedOtherItems = updatedQueue.filter(
    (item) => isSyncable(item) && item.type !== "photo" && !(item.type === "equipment")
  );

  // documentKey 기준으로 그룹화 (equipment create 제외한 나머지)
  const groups = new Map<string, SyncQueueItem[]>();
  for (const item of updatedOtherItems) {
    const key = getDocumentKey(item);
    const group = groups.get(key);
    if (group) {
      group.push(item);
    } else {
      groups.set(key, [item]);
    }
  }

  // 각 그룹을 병렬 처리 (그룹 내부는 순차)
  const groupEntries = Array.from(groups.values());
  const poolResults = await promisePool(groupEntries, 3, async (groupItems) => {
    const groupResults: {
      results: ItemSyncResult[];
      conflicts: ConflictInfo[];
      syncedCount: number;
      failedCount: number;
      conflictCount: number;
    } = { results: [], conflicts: [], syncedCount: 0, failedCount: 0, conflictCount: 0 };

    // 그룹 내 항목은 순차 처리 (같은 문서의 버전 충돌 방지)
    // 버전 전파: 성공한 mutation의 newVersion을 후속 항목에 적용
    let latestVersion: number | undefined;

    for (const item of groupItems) {
      current++;
      options.onItemProgress?.(current, total, item);

      // 이전 항목에서 전파된 버전이 있으면 적용
      if (latestVersion != null && item.expectedVersion != null) {
        item.expectedVersion = latestVersion;
      }

      // 충돌 감지 (machine 타입만)
      if (fetchServerData && item.type === "machine") {
        const serverData = await fetchServerData(item.type, item.entityId);

        if (serverData) {
          const conflictInfo = detectConflict(item.payload as never, serverData as never);

          if (conflictInfo.hasConflict) {
            if (autoResolveConflicts) {
              const resolved = resolveConflict(conflictInfo, defaultResolution);
              item.payload = { ...item.payload, ...resolved };
            } else {
              groupResults.conflicts.push(conflictInfo);
              groupResults.conflictCount++;
              groupResults.results.push({
                itemId: item.id,
                status: "conflict",
                conflict: conflictInfo,
              });
              continue;
            }
          }
        }
      }

      const result = await syncSingleItem(item, mutate, fetchMachineDocument, options);
      groupResults.results.push(result);

      if (result.status === "synced") {
        groupResults.syncedCount++;
        await updateSyncQueueItem(item.id, {
          status: "completed",
          syncedAt: Date.now(),
        });
        // 버전 전파: 성공 시 newVersion을 다음 항목에 전달
        if (result.newVersion != null) {
          latestVersion = result.newVersion;
        }

        // measure delete 성공 시: 후속 큐 항목의 measureIndex 보정 (인덱스 shift)
        // 삭제된 인덱스보다 큰 measureIndex를 가진 같은 measureType 항목은 -1
        // 보정된 값은 IndexedDB에도 저장하여 크래시 시 데이터 정합성 유지
        if (item.type === "measure" && item.operation === "delete") {
          const deletedIndex = item.payload.measureIndex as number;
          const deletedMeasureType = item.payload.measureType;

          for (const laterItem of groupItems) {
            // measure 항목 인덱스 보정 (기존 로직)
            if (
              laterItem.type === "measure" &&
              laterItem.id !== item.id &&
              laterItem.payload.measureType === deletedMeasureType &&
              (!laterItem.status || laterItem.status === "pending") &&
              typeof laterItem.payload.measureIndex === "number" &&
              laterItem.payload.measureIndex > deletedIndex
            ) {
              laterItem.payload.measureIndex--;
              logger.info(
                `[인덱스 보정] ${laterItem.id}: measureIndex ${laterItem.payload.measureIndex + 1} → ${laterItem.payload.measureIndex}`
              );
              // IndexedDB에 보정된 인덱스 저장 (크래시 복구 대비)
              await updateSyncQueueItem(laterItem.id, {
                payload: laterItem.payload,
              });
            }
          }

          // 참고: 사진 큐 보정은 useMeasures.ts의 삭제 시점에서 즉시 처리됨 (#458)
        }
      } else {
        groupResults.failedCount++;
        await updateSyncQueueItem(item.id, {
          status: "failed",
          retryCount: item.retryCount + 1,
          lastError: result.error,
        });
      }
    }

    return groupResults;
  });

  // 그룹별 결과 집계
  for (const poolResult of poolResults) {
    if (poolResult.status === "fulfilled" && poolResult.value) {
      results.push(...poolResult.value.results);
      conflicts.push(...poolResult.value.conflicts);
      syncedCount += poolResult.value.syncedCount;
      failedCount += poolResult.value.failedCount;
      conflictCount += poolResult.value.conflictCount;
    } else if (poolResult.status === "rejected") {
      // 그룹 전체 실패 시 (예상치 못한 오류)
      logger.error("동기화 그룹 실행 실패:", poolResult.reason);
      failedCount++;
    }
  }

  return {
    success: failedCount === 0 && conflictCount === 0,
    syncedCount,
    failedCount,
    conflictCount,
    results,
    conflicts,
  };
}

/**
 * 특정 타입의 항목만 동기화
 * 병렬 처리 (concurrency: 3)
 */
export async function syncByType(
  type: SyncQueueItem["type"],
  mutate: MutateFunction,
  fetchMachineDocument: FetchMachineDocumentFunction,
  options: SyncOptions = {}
): Promise<SyncExecutionResult> {
  const queue = await getSyncQueue();
  const filteredQueue = queue.filter((item) => item.type === type && isSyncable(item));

  const allResults: ItemSyncResult[] = [];
  let syncedCount = 0;
  let failedCount = 0;

  const poolResults = await promisePool(filteredQueue, 3, async (item) => {
    const result = await syncSingleItem(item, mutate, fetchMachineDocument, options);

    if (result.status === "synced") {
      await updateSyncQueueItem(item.id, {
        status: "completed",
        syncedAt: Date.now(),
      });
    } else {
      await updateSyncQueueItem(item.id, {
        status: "failed",
        retryCount: item.retryCount + 1,
        lastError: result.error,
      });
    }

    return result;
  });

  for (const poolResult of poolResults) {
    if (poolResult.status === "fulfilled" && poolResult.value) {
      allResults.push(poolResult.value);
      if (poolResult.value.status === "synced") {
        syncedCount++;
      } else {
        failedCount++;
      }
    } else if (poolResult.status === "rejected") {
      failedCount++;
    }
  }

  return {
    success: failedCount === 0,
    syncedCount,
    failedCount,
    conflictCount: 0,
    results: allResults,
    conflicts: [],
  };
}

/**
 * 충돌 해결 후 동기화 재시도
 */
export async function resolveConflictAndSync(
  itemId: string,
  resolution: ConflictResolution,
  conflict: ConflictInfo,
  mutate: MutateFunction,
  fetchMachineDocument: FetchMachineDocumentFunction,
  options: SyncOptions = {}
): Promise<ItemSyncResult> {
  // 충돌 해결
  const resolved = resolveConflict(conflict, resolution);

  // 해결된 데이터로 동기화 시도
  const queue = await getSyncQueue();
  const item = queue.find((i) => i.id === itemId);

  if (!item) {
    return {
      itemId,
      status: "failed",
      error: "Item not found in queue",
    };
  }

  // 해결된 데이터로 payload 업데이트
  const updatedItem: SyncQueueItem = {
    ...item,
    payload: { ...item.payload, ...resolved },
  };

  const result = await syncSingleItem(updatedItem, mutate, fetchMachineDocument, options);

  if (result.status === "synced") {
    await updateSyncQueueItem(itemId, {
      status: "completed",
      syncedAt: Date.now(),
    });
  }

  return result;
}

/**
 * photoEtc 동기화 후 pictures 배열 업데이트 콜백
 */
export type PhotoEtcUpdateCallback = (params: {
  projectId: string;
  docKey: string;
  machineId: string;
  uploadedUrl: string;
}) => Promise<boolean>;

/**
 * 계측 사진 동기화 후 measure photo URL 업데이트 콜백
 */
export type MeasurePhotoUpdateCallback = (params: {
  projectId: string;
  docKey: string;
  machineId: string;
  measureType: string;
  measureIndex: number;
  slot: "check" | "result";
  uploadedUrl: string;
}) => Promise<boolean>;

/**
 * 사진 동기화 전용 함수
 * 로컬 파일을 읽어 서버에 업로드하고 정리
 *
 * checkItem 사진: 병렬 업로드 (concurrency: 2) - pictures 배열 업데이트 불필요
 * photoEtc 사진: 같은 문서(projectId+docKey+machineId) 기준 순차 처리
 *   → VERSION_CONFLICT 방지 (pictures 배열에 순차적으로 URL 추가)
 *   → 서로 다른 문서 그룹 간에는 병렬 처리 (concurrency: 2)
 * measurePhoto 사진: 같은 문서 기준 순차 처리
 *   → 업로드 후 updateMeasure로 photo URL 업데이트 (VERSION_CONFLICT 방지)
 *
 * @param onPhotoEtcUploaded photoEtc 업로드 성공 시 pictures 배열 업데이트 콜백
 * @param onMeasurePhotoUploaded measurePhoto 업로드 성공 시 measure photo URL 업데이트 콜백
 */
export async function syncPhotos(
  onPhotoEtcUploaded?: PhotoEtcUpdateCallback,
  onMeasurePhotoUploaded?: MeasurePhotoUpdateCallback
): Promise<SyncExecutionResult> {
  const queue = await getSyncQueue();
  const photoItems = queue.filter((item) => item.type === "photo" && isSyncable(item));

  const allResults: ItemSyncResult[] = [];
  let syncedCount = 0;
  let failedCount = 0;

  // 사진 유형별 분리
  const getUploadType = (item: SyncQueueItem) =>
    (item.payload as { uploadType?: string })?.uploadType;

  const checkItemPhotos = photoItems.filter((item) => {
    const ut = getUploadType(item);
    return ut !== "photoEtc" && ut !== "measurePhoto";
  });
  const photoEtcItems = photoItems.filter((item) => getUploadType(item) === "photoEtc");
  const measurePhotoItems = photoItems.filter((item) => getUploadType(item) === "measurePhoto");

  // 1. checkItem 사진: 기존처럼 병렬 처리 (concurrency: 2)
  const checkItemResults = await promisePool(checkItemPhotos, 2, async (item) => {
    return syncSinglePhoto(item, onPhotoEtcUploaded, onMeasurePhotoUploaded);
  });

  for (const poolResult of checkItemResults) {
    if (poolResult.status === "fulfilled" && poolResult.value) {
      allResults.push(poolResult.value);
      if (poolResult.value.status === "synced") {
        syncedCount++;
      } else {
        failedCount++;
      }
    } else if (poolResult.status === "rejected") {
      failedCount++;
    }
  }

  // 2. photoEtc 사진: 같은 문서 기준 그룹화 후 그룹 내 순차 처리
  const photoEtcByDoc = new Map<string, SyncQueueItem[]>();
  for (const item of photoEtcItems) {
    const key = `${item.projectId}_${item.docKey}_${item.machineId}`;
    const group = photoEtcByDoc.get(key);
    if (group) {
      group.push(item);
    } else {
      photoEtcByDoc.set(key, [item]);
    }
  }

  const docGroups = Array.from(photoEtcByDoc.values());
  const docGroupResults = await promisePool(docGroups, 2, async (docPhotos) => {
    const results: ItemSyncResult[] = [];
    for (const item of docPhotos) {
      const result = await syncSinglePhoto(item, onPhotoEtcUploaded, onMeasurePhotoUploaded);
      results.push(result);
    }
    return results;
  });

  for (const poolResult of docGroupResults) {
    if (poolResult.status === "fulfilled" && poolResult.value) {
      for (const result of poolResult.value) {
        allResults.push(result);
        if (result.status === "synced") {
          syncedCount++;
        } else {
          failedCount++;
        }
      }
    } else if (poolResult.status === "rejected") {
      failedCount++;
    }
  }

  // 3. measurePhoto 사진: 같은 문서 기준 그룹화 후 그룹 내 순차 처리
  // (같은 설비의 measure update가 순차적으로 처리되어야 VERSION_CONFLICT 방지)
  const measurePhotoByDoc = new Map<string, SyncQueueItem[]>();
  for (const item of measurePhotoItems) {
    const key = `${item.projectId}_${item.docKey}_${item.machineId}`;
    const group = measurePhotoByDoc.get(key);
    if (group) {
      group.push(item);
    } else {
      measurePhotoByDoc.set(key, [item]);
    }
  }

  const measureDocGroups = Array.from(measurePhotoByDoc.values());
  const measureDocGroupResults = await promisePool(measureDocGroups, 2, async (docPhotos) => {
    const results: ItemSyncResult[] = [];
    for (const item of docPhotos) {
      const result = await syncSinglePhoto(item, onPhotoEtcUploaded, onMeasurePhotoUploaded);
      results.push(result);
    }
    return results;
  });

  for (const poolResult of measureDocGroupResults) {
    if (poolResult.status === "fulfilled" && poolResult.value) {
      for (const result of poolResult.value) {
        allResults.push(result);
        if (result.status === "synced") {
          syncedCount++;
        } else {
          failedCount++;
        }
      }
    } else if (poolResult.status === "rejected") {
      failedCount++;
    }
  }

  return {
    success: failedCount === 0,
    syncedCount,
    failedCount,
    conflictCount: 0,
    results: allResults,
    conflicts: [],
  };
}

/**
 * 단일 사진 동기화 처리 (syncPhotos에서 호출)
 *
 * photoEtc 재시도 흐름:
 * - 파일 업로드는 성공했지만 pictures 배열 업데이트가 실패한 경우,
 *   LocalPhoto.serverUrl이 이미 설정되어 있으므로 파일 업로드를 건너뛰고
 *   onPhotoEtcUploaded 콜백만 재호출한다.
 */
async function syncSinglePhoto(
  item: SyncQueueItem,
  onPhotoEtcUploaded?: PhotoEtcUpdateCallback,
  onMeasurePhotoUploaded?: MeasurePhotoUpdateCallback
): Promise<ItemSyncResult> {
  try {
    // 1. LocalPhoto 메타데이터 조회
    const photo = await getLocalPhoto(item.entityId);
    if (!photo) {
      await removeSyncQueueItem(item.id);
      return {
        itemId: item.id,
        status: "failed",
        error: "LocalPhoto not found",
      };
    }

    const payload = item.payload as {
      checkId?: string;
      fieldName?: "image" | "imageO" | "imageX";
      uploadType?: "photoEtc" | "measurePhoto";
      pictureIndex?: number;
      measureType?: string;
      measureIndex?: number;
      slot?: "check" | "result";
    };

    // 1.5a. 이미 서버에 업로드된 measurePhoto 사진인 경우 (재시도 시)
    if (payload.uploadType === "measurePhoto" && photo.serverUrl) {
      logger.info("이미 업로드된 measurePhoto 사진 - measure 업데이트만 재시도", {
        photoId: photo.id,
        serverUrl: photo.serverUrl,
      });

      if (
        onMeasurePhotoUploaded &&
        payload.measureType &&
        payload.slot != null &&
        payload.measureIndex != null
      ) {
        try {
          const updated = await onMeasurePhotoUploaded({
            projectId: item.projectId!,
            docKey: item.docKey!,
            machineId: item.machineId!,
            measureType: payload.measureType,
            measureIndex: payload.measureIndex,
            slot: payload.slot,
            uploadedUrl: photo.serverUrl,
          });

          if (updated) {
            await updateLocalPhoto(photo.id, {
              synced: true,
              syncStatus: "synced",
              uploadedAt: Date.now(),
              localPath: "",
              base64Thumbnail: undefined,
              base64Data: undefined,
            });
            await removeSyncQueueItem(item.id);
            return { itemId: item.id, status: "synced" };
          }
        } catch (callbackError) {
          logger.warn("measure photo URL 재시도 실패:", callbackError);
        }
      }

      const errorMsg = "measure photo URL 업데이트 재시도 실패";
      await updateSyncQueueItem(item.id, {
        status: "failed",
        retryCount: item.retryCount + 1,
        lastError: errorMsg,
      });
      return { itemId: item.id, status: "failed", error: errorMsg };
    }

    // 1.5b. 이미 서버에 업로드된 photoEtc 사진인 경우 (재시도 시)
    // 파일 업로드를 건너뛰고 pictures 배열 업데이트만 재시도
    if (payload.uploadType === "photoEtc" && photo.serverUrl) {
      logger.info("이미 업로드된 photoEtc 사진 - pictures 업데이트만 재시도", {
        photoId: photo.id,
        serverUrl: photo.serverUrl,
      });

      if (onPhotoEtcUploaded) {
        try {
          const updated = await onPhotoEtcUploaded({
            projectId: item.projectId!,
            docKey: item.docKey!,
            machineId: item.machineId!,
            uploadedUrl: photo.serverUrl,
          });

          if (updated) {
            // 로컬 메타데이터 정리 (synced 마킹 + 로컬 파일/base64 제거)
            await updateLocalPhoto(photo.id, {
              synced: true,
              syncStatus: "synced",
              uploadedAt: Date.now(),
              localPath: "",
              base64Thumbnail: undefined,
              base64Data: undefined,
            });
            await removeSyncQueueItem(item.id);
            return { itemId: item.id, status: "synced" };
          }
        } catch (callbackError) {
          // 아래에서 failed 처리
          logger.warn("pictures 배열 재시도 실패:", callbackError);
        }
      }

      // pictures 업데이트 실패 → failed로 마킹하여 다음 재시도 가능
      const errorMsg = "pictures 배열 업데이트 재시도 실패";
      await updateSyncQueueItem(item.id, {
        status: "failed",
        retryCount: item.retryCount + 1,
        lastError: errorMsg,
      });
      return { itemId: item.id, status: "failed", error: errorMsg };
    }

    // 2. 이미지 데이터 가져오기
    let dataUrl: string;

    // 웹 환경: IndexedDB에 저장된 base64Data 사용
    if (photo.localPath.startsWith("indexeddb://")) {
      if (!photo.base64Data) {
        logger.error("웹 환경에서 base64Data가 없음");
        await updateSyncQueueItem(item.id, {
          status: "failed",
          retryCount: item.retryCount + 1,
          lastError: "이미지 데이터가 없습니다",
        });
        return {
          itemId: item.id,
          status: "failed",
          error: "No base64Data for web photo",
        };
      }
      dataUrl = photo.base64Data;
    } else {
      // 네이티브 환경: Capacitor Filesystem에서 읽기
      let fileData: string;
      try {
        const readResult = await Filesystem.readFile({
          path: photo.localPath.replace(/^file:\/\//, ""),
          directory: Directory.Data,
        });
        fileData = typeof readResult.data === "string" ? readResult.data : "";
      } catch (readError) {
        // 파일 경로가 절대 경로인 경우 직접 읽기 시도
        try {
          const readResult = await Filesystem.readFile({
            path: photo.localPath,
          });
          fileData = typeof readResult.data === "string" ? readResult.data : "";
        } catch (retryError) {
          logger.error("로컬 파일 읽기 실패:", { readError, retryError });
          await updateSyncQueueItem(item.id, {
            status: "failed",
            retryCount: item.retryCount + 1,
            lastError: "로컬 파일을 찾을 수 없습니다",
          });
          return {
            itemId: item.id,
            status: "failed",
            error: "Local file not found",
          };
        }
      }
      dataUrl = `data:${photo.mimeType};base64,${fileData}`;
    }

    // 3. base64 -> File 변환
    const file = dataUrlToFile(dataUrl, photo.fileName);

    // 4. 서버 업로드 (checkItem vs photoEtc 분기)
    let uploadResult;

    if (payload.uploadType === "photoEtc") {
      uploadResult = await uploadImage({
        file,
        type: "photoEtc",
        projectId: item.projectId!,
        docKey: item.docKey,
        machineId: item.machineId,
      });
    } else if (payload.uploadType === "measurePhoto") {
      // 계측 사진: document 타입으로 업로드 (기존 온라인 경로와 동일)
      uploadResult = await uploadImage({
        file,
        type: "document",
        projectId: item.projectId!,
        docKey: item.docKey,
        machineId: item.machineId,
      });
    } else {
      uploadResult = await uploadImage({
        file,
        type: "checkItem",
        projectId: item.projectId!,
        docKey: item.docKey,
        machineId: item.machineId,
        checkId: payload.checkId,
        ...(payload.fieldName === "imageO" && { imageO: "O" as const }),
        ...(payload.fieldName === "imageX" && { imageX: "X" as const }),
      });
    }

    if (uploadResult.success && uploadResult.url) {
      // 5. 성공: 로컬 파일 삭제 (네이티브 환경만)
      if (!photo.localPath.startsWith("indexeddb://")) {
        try {
          await Filesystem.deleteFile({
            path: photo.localPath.replace(/^file:\/\//, ""),
            directory: Directory.Data,
          });
        } catch (firstDeleteError) {
          try {
            await Filesystem.deleteFile({
              path: photo.localPath,
            });
          } catch (retryDeleteError) {
            logger.warn("로컬 파일 삭제 실패 (무시됨):", {
              firstDeleteError,
              retryDeleteError,
            });
          }
        }
      }

      // 6. photoEtc인 경우 pictures 배열 업데이트를 먼저 시도
      if (payload.uploadType === "photoEtc" && onPhotoEtcUploaded) {
        let picturesUpdated = false;
        try {
          picturesUpdated = await onPhotoEtcUploaded({
            projectId: item.projectId!,
            docKey: item.docKey!,
            machineId: item.machineId!,
            uploadedUrl: uploadResult.url,
          });
        } catch (callbackError) {
          const errorMessage =
            callbackError instanceof Error ? callbackError.message : "pictures 업데이트 오류";
          logger.error("pictures 배열 업데이트 실패:", callbackError);

          // 파일은 업로드되었으므로 serverUrl 저장 (재시도 시 파일 업로드 건너뛰기 위해)
          await updateLocalPhoto(photo.id, {
            serverUrl: uploadResult.url,
            uploadedAt: Date.now(),
          });
          await updateSyncQueueItem(item.id, {
            status: "failed",
            retryCount: item.retryCount + 1,
            lastError: errorMessage,
          });
          return { itemId: item.id, status: "failed", error: errorMessage };
        }

        if (!picturesUpdated) {
          // pictures 업데이트 실패 → serverUrl 저장 후 failed 마킹
          logger.warn("pictures 배열 업데이트 실패 (false 반환)");
          await updateLocalPhoto(photo.id, {
            serverUrl: uploadResult.url,
            uploadedAt: Date.now(),
          });
          const errorMsg = "pictures 배열 업데이트 실패";
          await updateSyncQueueItem(item.id, {
            status: "failed",
            retryCount: item.retryCount + 1,
            lastError: errorMsg,
          });
          return { itemId: item.id, status: "failed", error: errorMsg };
        }
      }

      // 6b. measurePhoto인 경우 measure photo URL 업데이트
      if (
        payload.uploadType === "measurePhoto" &&
        onMeasurePhotoUploaded &&
        payload.measureType &&
        payload.slot != null &&
        payload.measureIndex != null
      ) {
        let measureUpdated = false;
        try {
          measureUpdated = await onMeasurePhotoUploaded({
            projectId: item.projectId!,
            docKey: item.docKey!,
            machineId: item.machineId!,
            measureType: payload.measureType,
            measureIndex: payload.measureIndex,
            slot: payload.slot,
            uploadedUrl: uploadResult.url,
          });
        } catch (callbackError) {
          const errorMessage =
            callbackError instanceof Error ? callbackError.message : "measure photo 업데이트 오류";
          logger.error("measure photo URL 업데이트 실패:", callbackError);

          // 파일은 업로드되었으므로 serverUrl 저장 (재시도 시 파일 업로드 건너뛰기 위해)
          await updateLocalPhoto(photo.id, {
            serverUrl: uploadResult.url,
            uploadedAt: Date.now(),
          });
          await updateSyncQueueItem(item.id, {
            status: "failed",
            retryCount: item.retryCount + 1,
            lastError: errorMessage,
          });
          return { itemId: item.id, status: "failed", error: errorMessage };
        }

        if (!measureUpdated) {
          logger.warn("measure photo URL 업데이트 실패 (false 반환)");
          await updateLocalPhoto(photo.id, {
            serverUrl: uploadResult.url,
            uploadedAt: Date.now(),
          });
          const errorMsg = "measure photo URL 업데이트 실패";
          await updateSyncQueueItem(item.id, {
            status: "failed",
            retryCount: item.retryCount + 1,
            lastError: errorMsg,
          });
          return { itemId: item.id, status: "failed", error: errorMsg };
        }
      }

      // 7. LocalPhoto 업데이트 (pictures/measure 업데이트 성공 후)
      await updateLocalPhoto(photo.id, {
        synced: true,
        syncStatus: "synced",
        serverUrl: uploadResult.url,
        uploadedAt: Date.now(),
        localPath: "",
        base64Data: undefined,
      });

      // 8. 큐에서 제거
      await removeSyncQueueItem(item.id);

      return {
        itemId: item.id,
        status: "synced",
      };
    } else {
      // 업로드 실패
      await updateSyncQueueItem(item.id, {
        status: "failed",
        retryCount: item.retryCount + 1,
        lastError: uploadResult.error || "업로드 실패",
      });
      return {
        itemId: item.id,
        status: "failed",
        error: uploadResult.error,
      };
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    await updateSyncQueueItem(item.id, {
      status: "failed",
      retryCount: item.retryCount + 1,
      lastError: errorMessage,
    });
    return {
      itemId: item.id,
      status: "failed",
      error: errorMessage,
    };
  }
}

/**
 * 동기화 완료된 사진 메타데이터 정리
 * synced=true이고 serverUrl이 있는 LocalPhoto 삭제
 */
export async function cleanupSyncedPhotoMetadata(): Promise<number> {
  const { cleanupSyncedPhotos } = await import("./photos");
  return cleanupSyncedPhotos();
}

// ============================================
// Feature 288: 설비 템플릿 동기화
// ============================================

import { apolloClient } from "../apollo";
import {
  MACHINE_TEMPLATES_QUERY,
  type MachineTemplatesData,
  type MachineTemplateResponse,
} from "../graphql/equipment";
import { saveMachineTemplates, isTemplateCacheValid, getTemplateCacheCount } from "./templates";
import type { CachedMachineTemplate } from "./types";

/**
 * API 응답을 캐시 형식으로 변환
 */
function toMachineTemplateCaches(templates: MachineTemplateResponse[]): CachedMachineTemplate[] {
  const now = Date.now();
  return templates.map((t) => ({
    key: t.key,
    sectionTitle: t.sectionTitle,
    checkListTemplate: t.checkListTemplate.map((item) => ({
      title: item.title,
      result: item.result,
      group: item.group,
      syncId: item.syncId,
      inspectionMethod: item.inspectionMethod ?? [],
      inspectionMethodImages: item.inspectionMethodImages ?? [],
      inspectionCriteria: item.inspectionCriteria ?? [],
      compliantTemplate: item.compliantTemplate ?? "",
      nonCompliantTemplate: item.nonCompliantTemplate ?? "",
    })),
    maintenanceTemplate: t.maintenanceTemplate.map((item) => ({
      title: item.title,
      result: item.result,
      group: item.group,
      syncId: item.syncId,
      inspectionMethod: item.inspectionMethod ?? [],
      inspectionMethodImages: item.inspectionMethodImages ?? [],
      inspectionCriteria: item.inspectionCriteria ?? [],
      compliantTemplate: item.compliantTemplate ?? "",
      nonCompliantTemplate: item.nonCompliantTemplate ?? "",
    })),
    dataTemplate: t.dataTemplate.map((item) => ({
      section: item.section,
      title: item.title,
      unit: item.unit ?? "",
      calKey: item.calKey ?? "",
      enableInputDesignValue: item.enableInputDesignValue,
      enableInputCheckValue: item.enableInputCheckValue,
      bgColor: item.bgColor ?? "",
    })),
    docFormat: t.docFormat
      ? {
          check: t.docFormat.check ?? "",
          abnormal: t.docFormat.abnormal ?? "",
          photoHeight: t.docFormat.photoHeight ?? "",
          etc: t.docFormat.etc ?? "",
        }
      : undefined,
    docFormatMT: t.docFormatMT
      ? {
          check: t.docFormatMT.check ?? "",
          abnormal: t.docFormatMT.abnormal ?? "",
          photoHeight: t.docFormatMT.photoHeight ?? "",
          etc: t.docFormatMT.etc ?? "",
        }
      : undefined,
    howToWrite: t.howToWrite ?? "",
    howToWriteMT: t.howToWriteMT ?? "",
    etcContent: t.etcContent ?? "",
    cachedAt: now,
  }));
}

/**
 * 설비 템플릿 동기화 결과
 */
interface TemplateSyncResult {
  success: boolean;
  syncedCount: number;
  error?: string;
  skipped?: boolean;
}

/**
 * 설비 템플릿 동기화 (서버 → 로컬 IndexedDB)
 * 온라인 상태에서 호출하여 오프라인 설비 생성에 필요한 템플릿 캐싱
 * @param force 캐시 유효성 무시하고 강제 갱신
 */
export async function syncMachineTemplates(force: boolean = false): Promise<TemplateSyncResult> {
  try {
    // 캐시가 유효하면 갱신 생략 (force가 아닌 경우)
    if (!force) {
      const valid = await isTemplateCacheValid();
      if (valid) {
        const count = await getTemplateCacheCount();
        return {
          success: true,
          syncedCount: count,
          skipped: true,
        };
      }
    }

    // GraphQL 쿼리 실행
    const { data, error } = await apolloClient.query<MachineTemplatesData>({
      query: MACHINE_TEMPLATES_QUERY,
      fetchPolicy: "network-only",
    });

    if (error) {
      return {
        success: false,
        syncedCount: 0,
        error: error.message,
      };
    }

    if (!data?.machineTemplates) {
      return {
        success: false,
        syncedCount: 0,
        error: "템플릿 데이터가 없습니다",
      };
    }

    // 캐시 형식으로 변환 후 저장
    const caches = toMachineTemplateCaches(data.machineTemplates);
    await saveMachineTemplates(caches);

    return {
      success: true,
      syncedCount: caches.length,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : "알 수 없는 오류";
    return {
      success: false,
      syncedCount: 0,
      error: message,
    };
  }
}

/**
 * ItemId 매핑 저장 (checkList, dataList, maintenance)
 * Feature 326: equipment create 후 서버 문서 조회하여 itemId 매핑
 */
async function saveItemIdMappings(
  projectId: string | undefined,
  docKey: string | undefined,
  localMachineId: string,
  serverMachineId: string,
  fetchMachineDocument: FetchMachineDocumentFunction
): Promise<void> {
  if (!projectId || !docKey) {
    logger.warn("projectId 또는 docKey가 없어 itemId 매핑을 건너뜁니다");
    return;
  }

  try {
    // 1. 서버 문서 조회
    const serverDoc = await fetchMachineDocument(projectId, docKey, serverMachineId);

    logger.debug(`[ItemId 매핑] 서버 문서 조회 결과`, {
      checkListLength: serverDoc.checkList?.length || 0,
      dataListLength: serverDoc.dataList?.length || 0,
      maintenanceLength: serverDoc.maintenance?.length || 0,
    });

    // 2. checkList 매핑 저장 (로컬 ID 패턴으로 생성)
    const serverCheckList = serverDoc.checkList || [];
    for (let i = 0; i < serverCheckList.length; i++) {
      const localItemId = `check-${localMachineId}-${i}`;
      const serverItemId = serverCheckList[i].id;

      if (serverItemId) {
        await saveIdMapping({
          localId: localItemId,
          serverId: serverItemId,
          entityType: "checkListItem",
        });
        logger.debug(`[ItemId 매핑] checkList[${i}]: ${localItemId} → ${serverItemId}`);
      }
    }
    if (serverCheckList.length > 0) {
      logger.info(`[ItemId 매핑] checkList ${serverCheckList.length}개 매핑 완료`);
    }

    // 3. dataList 매핑 저장 (로컬 ID 패턴으로 생성)
    const serverDataList = serverDoc.dataList || [];
    for (let i = 0; i < serverDataList.length; i++) {
      const localItemId = `data-${localMachineId}-${i}`;
      const serverItemId = serverDataList[i].id;

      if (serverItemId) {
        await saveIdMapping({
          localId: localItemId,
          serverId: serverItemId,
          entityType: "dataItem",
        });
        logger.debug(`[ItemId 매핑] dataList[${i}]: ${localItemId} → ${serverItemId}`);
      }
    }
    if (serverDataList.length > 0) {
      logger.info(`[ItemId 매핑] dataList ${serverDataList.length}개 매핑 완료`);
    }

    // 4. maintenance 매핑 저장 (로컬 ID 패턴으로 생성, 있는 경우만)
    const serverMaintenance = serverDoc.maintenance || [];
    for (let i = 0; i < serverMaintenance.length; i++) {
      const localItemId = `mt-${localMachineId}-${i}`;
      const serverItemId = serverMaintenance[i].id;

      if (serverItemId) {
        await saveIdMapping({
          localId: localItemId,
          serverId: serverItemId,
          entityType: "maintenanceItem",
        });
        logger.debug(`[ItemId 매핑] maintenance[${i}]: ${localItemId} → ${serverItemId}`);
      }
    }
    if (serverMaintenance.length > 0) {
      logger.info(`[ItemId 매핑] maintenance ${serverMaintenance.length}개 매핑 완료`);
    }
  } catch (error) {
    logger.error("ItemId 매핑 저장 실패", error);
  }
}

/**
 * MACHINES 스토어의 로컬 ID를 서버 ID로 업데이트
 * Feature 326: 오프라인 설비 생성 후 ID 매핑
 */
async function updateMachineIdInCache(
  localId: string,
  serverId: string,
  projectId?: string
): Promise<void> {
  if (!projectId) {
    logger.warn("projectId가 없어 캐시 업데이트를 건너뜁니다", { localId, serverId });
    return;
  }

  const cacheKey = `${projectId}_${localId}`;
  const machine = await getRecord<CachedMachine>(STORES.MACHINES, cacheKey);

  if (machine) {
    // 기존 항목 삭제
    await deleteRecord(STORES.MACHINES, cacheKey);

    // 새 ID로 저장 (복합 키와 machine._id 모두 업데이트)
    const newKey = `${projectId}_${serverId}`;
    machine._id = newKey; // 복합 키
    machine.machine._id = serverId; // 단순 machineId
    await putRecord(STORES.MACHINES, machine);

    logger.info("캐시 ID 업데이트 완료", { localId, serverId, projectId });
  } else {
    logger.debug("캐시에서 설비를 찾을 수 없음", { cacheKey });
  }
}

/**
 * SYNC_QUEUE의 관련 항목들의 machineId 및 itemId 업데이트
 * Feature 326: 후속 동기화 항목의 ID 자동 변환
 */
async function updateMachineIdInSyncQueue(localId: string, serverId: string): Promise<void> {
  const queue = await getAllRecords<SyncQueueItem>(STORES.SYNC_QUEUE);
  let updatedCount = 0;

  for (const item of queue) {
    let updated = false;

    if (item.machineId === localId) {
      item.machineId = serverId;
      updated = true;
    }
    if (item.entityId === localId) {
      item.entityId = serverId;
      updated = true;
    }

    // patches 배열의 itemId도 변환 (check-offline-xxx-0 → 서버 ObjectId)
    if (item.payload.patches && Array.isArray(item.payload.patches)) {
      for (const patch of item.payload.patches) {
        // itemId 형식: check-offline-xxx-0, data-offline-xxx-1, mt-offline-xxx-2
        // 정확한 매칭을 위해 startsWith 사용 (includes는 부분 문자열 매칭 오류 가능)
        if (
          patch.itemId?.startsWith(`check-${localId}-`) ||
          patch.itemId?.startsWith(`data-${localId}-`) ||
          patch.itemId?.startsWith(`mt-${localId}-`)
        ) {
          const itemIdMapping = await getIdMapping(patch.itemId);
          if (itemIdMapping) {
            logger.info(`[ItemId 변환] ${patch.itemId} → ${itemIdMapping.serverId}`, {
              queueItemId: item.id,
            });
            patch.itemId = itemIdMapping.serverId;
            updated = true;
          } else {
            logger.warn(`[ItemId 변환] 매핑 없음: ${patch.itemId}`, { queueItemId: item.id });
          }
        }
      }
    }

    if (updated) {
      await putRecord(STORES.SYNC_QUEUE, item);
      updatedCount++;
    }
  }

  if (updatedCount > 0) {
    logger.info("동기화 큐 ID 업데이트 완료", { localId, serverId, updatedCount });
  }
}

// Export types for external use
export type { SyncExecutionResult, ItemSyncResult, FetchServerDataFunction, TemplateSyncResult };
